import * as React from "react";
import Drawer from "@mui/material/Drawer";
import {
  Box,
  Button,
  InputAdornment,
  styled,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import bfLogo from "../../../assets/images/utsav-pack-logo.png";

import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router";

import { checkPromo } from "../../../utilities/service";
import { useCartContext } from "../../../hooks/CartContext";
import { CouponItem } from "./CouponItem";
import { calculateDiscountAmount } from "../cartUtil";
import useApiMutation from "../../../hooks/useApiMutation";
import { toast } from "react-toastify";
import CouponCelebrationDialog from "./CouponCelebrationDialog";
import { useAuth } from "../../../hooks/AuthContext";

export const removeCoupon = (setApplyDiscount, msg) => {
  setApplyDiscount({
    code: null,
    percentage: null,
    type: null,
    save: null,
    min_value: null,
    max_value: null,
  });
  toast.success(msg);
};

export default function CouponSideNav({
  handleCouponDrawer,
  open,
  cartSubTotal,
  backupTotalBill,
  promoList,
  appliedPromo,
  cartItems = [],
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dialogRef = useRef(null);
  const navigate = useNavigate();

  const { applyDiscount, setApplyDiscount } = useCartContext();
  const { user } = useAuth();

  // ✅ Only used in apply handlers — NOT in UI rendering
  const hasNormalProducts = cartItems.some((item) => !item?.parent_id);
  const isComboOnly = cartItems.length > 0 && !hasNormalProducts;
  const hasBulkProducts = cartItems.some((item) => item?.is_bulk_product > 0);
  const hasValidProducts = cartItems.some(
    (item) => !item?.parent_id && item?.is_bulk_product === 0,
  );

  const isInvalidCart = cartItems.length > 0 && !hasValidProducts;
  // ✅ Track last applied coupon meta for dialog
  const [lastAppliedCouponCode, setLastAppliedCouponCode] = useState(null);
  const [lastAppliedCouponCount, setLastAppliedCouponCount] = useState(null);
  const [lastAppliedCouponEndDate, setLastAppliedCouponEndDate] =
    useState(null);

  // ✅ Calculate total product discount saving across all normal (non-combo, non-bulk) items
  const totalProductDiscountSaving = cartItems.reduce((sum, item) => {
    if (item?.parent_id > 0) return sum;
    if (item?.is_bulk_product > 0) return sum;
    const originalPrice =
      parseFloat(item?.product_price || 0) *
      parseInt(item?.product_quantity || 1);
    const discountedPrice =
      parseFloat(item?.product_discount_price || 0) *
      parseInt(item?.product_quantity || 1);
    return sum + (originalPrice - discountedPrice);
  }, 0);

  // ✅ Original price total for normal items (base for coupon % calculation)
  const normalItemsOriginalTotal = cartItems.reduce((sum, item) => {
    if (item?.parent_id > 0) return sum;
    if (item?.is_bulk_product > 0) return sum;
    return (
      sum +
      parseFloat(item?.product_price || 0) *
        parseInt(item?.product_quantity || 1)
    );
  }, 0);

  // ✅ Check if a given coupon gives more saving than current product discounts
  const isCouponBetterThanProductDiscount = (coupon) => {
    if (!coupon) return false;

    // If no product has any discount, coupon is always better
    if (totalProductDiscountSaving <= 0) return true;

    let couponSaving = 0;
    if (coupon?.type === "PERCENTAGE") {
      couponSaving =
        (parseFloat(coupon?.discount) / 100) * normalItemsOriginalTotal;
    } else {
      couponSaving = parseFloat(coupon?.discount || 0);
    }

    return couponSaving > totalProductDiscountSaving;
  };

  const { mutate } = useApiMutation({
    mutationFn: ({ code, isCustomerPromo }) =>
      checkPromo(code, isCustomerPromo ? user?.customer_id : null),

    options: {
      onSuccess: (data, variables) => {
        if (data?.error) {
          toast.error(data?.message);
          setCouponLoader(false);
          return;
        }

        const allCoupons = [
          ...(promoList || []),
          ...(appliedPromo ? [appliedPromo] : []),
        ];

        const coupon = allCoupons.find(
          (item) => item.promo_code === variables.code,
        );

        if (!coupon) {
          toast.error("Coupon not found.");
          setCouponLoader(false);
          return;
        }

        const amount = calculateDiscountAmount(
          coupon?.discount,
          coupon?.type,
          cartSubTotal,
        );

        setApplyDiscount({
          code: coupon?.promo_code,
          percentage: coupon?.discount,
          type: coupon?.type,
          save: amount,
          min_value: coupon?.min_value ?? coupon?.min_amount ?? 0,
          max_value: coupon?.max_amount ?? null,
        });

        // ✅ Set all dialog meta from coupon object
        setLastAppliedCouponCode(coupon?.promo_code ?? null);
        setLastAppliedCouponCount(coupon?.promo_applicable_count ?? null);
        setLastAppliedCouponEndDate(coupon?.end_date ?? null);

        handleCouponDrawer(false)();
        dialogRef.current?.openDialog();
        setCouponLoader(false);
      },

      onError: (error) => {
        toast.error(error?.message);
        setCouponLoader(false);
      },
    },
  });

  const [couponDiscount, setCouponDiscount] = useState({
    amount: 0,
    type: "",
    percent: 0,
    code: "",
  });
  const [couponLoader, setCouponLoader] = useState(false);
  const [value, setValue] = useState("");
  const [amount, setAmount] = useState(0);

  const onChange = (e) => setValue(e.target.value);

  const removeCouponHandler = () => {
    removeCoupon(setApplyDiscount, `Coupon removed !!!`);
  };

  const applyCouponByTypingHandler = (value) => {
    if (isInvalidCart) {
      toast.info("Coupons are not applicable on combo & bulk products.");
      return;
    }

    const allCoupons = [
      ...(promoList || []),
      ...(appliedPromo ? [appliedPromo] : []),
    ];

    const couponStr = allCoupons.find(
      (item) => item?.promo_code?.toLowerCase() === value?.toLowerCase(),
    );

    if (couponStr) {
      // ✅ Silently block — no toast, button is already visually disabled
      if (!isCouponBetterThanProductDiscount(couponStr)) return;
      applyCouponHandler(couponStr);
    } else {
      setValue("");
      toast.error("Invalid Coupon.");
    }
  };

  const applyCouponHandler = (coupon) => {
    if (!coupon) return;

    if (isInvalidCart) {
      toast.info("Coupons are not applicable on combo & bulk products.");
      return;
    }

    const minAmount = coupon?.min_value ?? Number(coupon?.min_amount ?? 0);
    const maxAmount = coupon?.max_amount ? Number(coupon.max_amount) : null;

    if (applyDiscount?.code === coupon?.promo_code) {
      toast.info("Coupon already applied");
      return;
    }

    if (+cartSubTotal < minAmount) {
      const balance = minAmount - +cartSubTotal;
      toast.info(`Add ₹${balance} more to avail this offer`);
      return;
    }

    if (maxAmount !== null && +cartSubTotal > maxAmount) {
      toast.info(`This coupon is valid only up to ₹${maxAmount}`);
      return;
    }

    // ✅ Silently block — no toast, button is already visually disabled
    if (!isCouponBetterThanProductDiscount(coupon)) return;

    setCouponLoader(true);

    const amt = calculateDiscountAmount(
      coupon?.discount,
      coupon?.type,
      cartSubTotal,
    );

    setAmount(amt);

    mutate({
      code: coupon?.promo_code,
      isCustomerPromo: !!coupon?.cust_promo_id,
    });
  };

  useEffect(() => {
    if (applyDiscount != null) {
      const amount = calculateDiscountAmount(
        applyDiscount?.percentage,
        applyDiscount?.type,
        cartSubTotal,
      );

      setCouponDiscount((prev) => ({
        ...prev,
        amount: amount || 0,
        type: applyDiscount?.type,
        percent: applyDiscount?.percentage,
        code: applyDiscount?.code,
      }));
    }

    setValue("");
  }, [applyDiscount, cartSubTotal]);

  // ✅ Check typed coupon for Apply button disable state
  const typedCoupon = [
    ...(promoList || []),
    ...(appliedPromo ? [appliedPromo] : []),
  ].find((item) => item?.promo_code?.toLowerCase() === value?.toLowerCase());

  const isTypedCouponDisabled =
    value.length > 0 &&
    typedCoupon != null &&
    !isCouponBetterThanProductDiscount(typedCoupon);

  return (
    <>
      <Drawer open={open} anchor={"right"} onClose={handleCouponDrawer(false)}>
        <Box
          sx={{
            width: isMobile ? "100%" : "540px",
            justifyContent: "space-between",
            display: "flex",
            p: 4,
            flexDirection: "column",
            gap: 3,
            background: "var(--neutral-100)",
          }}
        >
          {/* Logo */}
          <Box sx={{ justifyContent: "center", display: "flex" }}>
            <Box
              component="img"
              src={bfLogo}
              alt="Utsav Pack"
              sx={{
                height: { xs: "60px", md: "70px", lg: "85px" },
                width: "auto",
              }}
            />
          </Box>

          <>
            <CouponInput
              type="text"
              fullWidth
              placeholder="Enter coupon code"
              variant="outlined"
              value={value}
              onChange={onChange}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {/* ✅ Light red + no click when disabled — no tooltip */}
                    <Button
                      onClick={() => applyCouponByTypingHandler(value)}
                      sx={{
                        color: isTypedCouponDisabled
                          ? "rgba(211, 47, 47, 0.35)"
                          : "red",
                        fontWeight: 600,
                        fontSize: "14px",
                        textTransform: "none",
                        pointerEvents: isTypedCouponDisabled ? "none" : "auto",
                        cursor: isTypedCouponDisabled ? "default" : "pointer",
                      }}
                    >
                      Apply
                    </Button>
                  </InputAdornment>
                ),
              }}
            />

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                mt: 2,
                backgroundColor: "var(--neutral-200)",
                padding: { xs: "8px 16px", md: "16px 32px" },
                textAlign: { xs: "center", sm: "left" },
                width: "calc(100% + 64px)",
                position: "relative",
                left: "-32px",
              }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: "12px", sm: "13px", md: "14px" },
                  lineHeight: "157%",
                  color: "var(--neutral-black)",
                }}
              >
                Available coupons
              </Typography>
            </Box>

            <Grid container spacing={3}>
              <Grid size={12}>
                {[...(promoList || []), ...(appliedPromo ? [appliedPromo] : [])]
                  .filter(
                    (v, i, arr) =>
                      arr.findIndex((x) => x.promo_code === v.promo_code) === i,
                  )
                  .map((item, index) => {
                    const isBetter = isCouponBetterThanProductDiscount(item);
                    return (
                      <CouponItem
                        key={item?.promo_code || index}
                        item={item}
                        couponDiscount={couponDiscount}
                        removeCouponHandler={removeCouponHandler}
                        applyCouponHandler={applyCouponHandler}
                        // ✅ Pass disabled — CouponItem handles light red styling
                        isDisabled={!isBetter}
                      />
                    );
                  })}
              </Grid>
            </Grid>
          </>
        </Box>
      </Drawer>

      <CouponCelebrationDialog
        ref={dialogRef}
        save={amount}
        couponCode={lastAppliedCouponCode}
        applicableCount={lastAppliedCouponCount}
        endDate={lastAppliedCouponEndDate}
      />
    </>
  );
}

const CouponInput = styled(TextField)({
  borderRadius: "8px",
  padding: "7px 12px",
  justifyContent: "center",
  display: "inline-flex",
  alignItems: "center",
  height: "56px",
  width: "100%",

  "& .MuiOutlinedInput-root": {
    fontSize: "14px !important",
    fontWeight: 600,
    color: "var(--neutral-black)",
    lineHeight: "157%",

    "& .MuiOutlinedInput-notchedOutline": {
      border: "1px solid var(--neutral-400)",
    },

    "&:hover .MuiOutlinedInput-notchedOutline": {
      border: "1px solid var(--neutral-600)",
    },

    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      border: "1px solid var(--neutral-500) !important",
    },
  },
});
